import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

const AbroadStudiesPage: React.FC = () => {
  useSEO();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white font-[Inter,Poppins,sans-serif] antialiased px-4 py-8">
      <h1 className="text-4xl md:text-6xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight drop-shadow-glow">Overseas Studies (Bangalore / Abroad)</h1>
      
      <div className="bg-white/5 rounded-2xl shadow-xl p-6 mb-8 border border-cyan-400/10 backdrop-blur-lg">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight drop-shadow-glow">Explore International Education Opportunities</h2>
        <p className="text-lg md:text-xl mb-4 text-cyan-200 drop-shadow-glow">
          Discover world-class education opportunities abroad. Our comprehensive guide helps you navigate 
          international admissions, scholarships, and visa requirements for studying in top global destinations.
        </p>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {/* Popular Destinations */}
          <div className="bg-black/40 border border-cyan-400/20 p-5 rounded-2xl shadow-md backdrop-blur-lg">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-cyan-400 drop-shadow-glow">Popular Destinations</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>United States</li>
              <li>United Kingdom</li>
              <li>Canada</li>
              <li>Australia</li>
              <li>Germany</li>
              <li>Singapore</li>
              <li>New Zealand</li>
            </ul>
          </div>
          
          {/* Services */}
          <div className="bg-black/40 border border-pink-400/20 p-5 rounded-2xl shadow-md backdrop-blur-lg">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-pink-400 drop-shadow-glow">Our Services</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>University Selection</li>
              <li>Application Assistance</li>
              <li>Visa Guidance</li>
              <li>Scholarship Information</li>
              <li>Language Test Preparation</li>
              <li>Pre-departure Briefing</li>
              <li>Accommodation Assistance</li>
            </ul>
          </div>
          
          {/* Requirements */}
          <div className="bg-black/40 border border-yellow-400/20 p-5 rounded-2xl shadow-md backdrop-blur-lg">
            <h3 className="text-xl md:text-2xl font-bold mb-3 text-yellow-400 drop-shadow-glow">General Requirements</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Academic Transcripts</li>
              <li>Language Proficiency (IELTS/TOEFL)</li>
              <li>Statement of Purpose</li>
              <li>Letters of Recommendation</li>
              <li>Financial Documents</li>
              <li>Entrance Exams (GRE, GMAT, etc.)</li>
              <li>Valid Passport</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-white/5 rounded-2xl shadow-xl p-6 mb-8 border border-cyan-400/10 backdrop-blur-lg">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight drop-shadow-glow">Why Study Abroad?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <h3 className="text-xl font-bold mb-2 text-cyan-400 drop-shadow-glow">Academic Benefits</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Access to world-class education</li>
              <li>Specialized programs not available locally</li>
              <li>Advanced research opportunities</li>
              <li>Internationally recognized qualifications</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-2 text-pink-400 drop-shadow-glow">Personal Growth</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Cultural exposure and global perspective</li>
              <li>Independence and self-reliance</li>
              <li>Language proficiency</li>
              <li>International network building</li>
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold mb-2 text-yellow-400 drop-shadow-glow">Career Benefits</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Enhanced resume and job prospects</li>
              <li>Global career opportunities</li>
              <li>Cross-cultural communication skills</li>
              <li>International industry connections</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-white/5 rounded-2xl shadow-xl p-6 border border-cyan-400/10 backdrop-blur-lg">
        <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight drop-shadow-glow">Contact Our Study Abroad Counselors</h2>
        <p className="text-lg md:text-xl mb-4 text-cyan-200 drop-shadow-glow">
          Our experienced counselors can guide you through every step of your international education journey.
          Schedule a consultation to get personalized advice.
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/abroad-consultancy" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition flex-1 text-center font-semibold">
            Apply for Study Abroad
          </Link>
          <Link to="/premium-services" className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-cyan-700 hover:to-blue-700 transition flex-1 text-center font-semibold">
            Domestic Consultancy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AbroadStudiesPage;
